import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatPKR } from '../api';

const brandHeader = (doc, title) => {
  doc.setFillColor(93, 58, 35);
  doc.rect(0, 0, 210, 28, 'F');
  doc.setTextColor(247, 243, 236);
  doc.setFontSize(18);
  doc.text('LUMORA', 14, 14);
  doc.setFontSize(10);
  doc.text(title, 14, 22);
  doc.setTextColor(46, 42, 38);
};

export const generateDispatchedWeeklyPDF = (orders, periodLabel) => {
  const doc = new jsPDF();
  brandHeader(doc, `Dispatched Orders — Weekly Report (${periodLabel})`);
  doc.setFontSize(10);
  doc.text(`Report Date: ${new Date().toLocaleDateString()}`, 14, 36);
  doc.text(`Total Orders: ${orders.length}`, 14, 42);

  autoTable(doc, {
    startY: 48,
    head: [['ID', 'Customer', 'Products', 'Qty', 'Address', 'Payment', 'Dispatch Date']],
    body: orders.map((o) => [
      `#${o.orderId}`,
      o.customerName,
      o.items.map((i) => i.productName).join(', '),
      o.items.reduce((s, i) => s + i.quantity, 0),
      `${o.address}, ${o.city}`,
      o.paymentMethod,
      o.date,
    ]),
    styles: { fontSize: 8 },
    headStyles: { fillColor: [139, 94, 60] },
  });

  const y = doc.lastAutoTable.finalY + 16;
  doc.text('Prepared By: ___________________', 14, y);
  doc.text('Signature: ___________________', 14, y + 10);
  doc.save(`lumora-dispatched-weekly-${periodLabel}.pdf`);
};

export const generateDispatchedMonthlyPDF = (orders, monthLabel, stats = {}) => {
  const doc = new jsPDF();
  brandHeader(doc, `Dispatched Orders — Monthly Report (${monthLabel})`);
  doc.setFontSize(10);
  doc.text(`Report Date: ${new Date().toLocaleDateString()}`, 14, 36);
  doc.text(`Monthly Revenue: ${formatPKR(stats.revenue || 0)}`, 14, 42);
  doc.text(`Total Orders: ${orders.length}`, 14, 48);
  doc.text(`Products Sold: ${stats.productsSold || 0}`, 14, 54);

  autoTable(doc, {
    startY: 60,
    head: [['ID', 'Customer', 'Products', 'Total', 'Dispatch Date', 'Payment']],
    body: orders.map((o) => [
      `#${o.orderId}`,
      o.customerName,
      o.items.map((i) => `${i.productName} x${i.quantity}`).join(', '),
      formatPKR(o.orderTotal),
      o.date,
      o.paymentMethod,
    ]),
    styles: { fontSize: 8 },
    headStyles: { fillColor: [139, 94, 60] },
  });

  doc.save(`lumora-dispatched-monthly-${monthLabel}.pdf`);
};

export const generateReturnedPDF = (orders, returns, periodLabel, type = 'weekly') => {
  const doc = new jsPDF();
  brandHeader(doc, `Returned Orders — ${type === 'weekly' ? 'Weekly' : 'Monthly'} Report (${periodLabel})`);

  const returnMap = {};
  returns.forEach((r) => {
    returnMap[r.orderId] = r;
  });

  autoTable(doc, {
    startY: 36,
    head: [['ID', 'Customer', 'Return Date', 'Reason', 'Refund Status', 'Products']],
    body: orders.map((o) => {
      const ret = returnMap[o.orderId] || {};
      return [
        `#${o.orderId}`,
        o.customerName,
        ret.date || o.date,
        ret.notes || '—',
        'Processed',
        o.items.map((i) => `${i.productName} x${i.quantity}`).join(', '),
      ];
    }),
    styles: { fontSize: 8 },
    headStyles: { fillColor: [139, 94, 60] },
  });

  doc.save(`lumora-returned-${type}-${periodLabel}.pdf`);
};

export const generateOwnerPeriodPDF = (finance, periodLabel, periodType = 'weekly') => {
  const doc = new jsPDF();
  const label = periodType === 'weekly' ? 'Weekly' : 'Monthly';
  brandHeader(doc, `${finance.owner.name} — ${label} Report (${periodLabel})`);
  doc.setFontSize(10);
  doc.text(`Report Date: ${new Date().toLocaleDateString()}`, 14, 36);
  doc.text(`Total Earnings: ${formatPKR(finance.totalEarnings)}`, 14, 44);
  doc.text(`Available Balance: ${formatPKR(finance.availableBalance)}`, 14, 50);

  const withdrawals = finance.withdrawals || [];

  autoTable(doc, {
    startY: 58,
    head: [['ID', 'Date', 'Amount', 'Method', 'Status', 'Notes']],
    body: withdrawals.map((w) => [
      w.id,
      w.date,
      formatPKR(w.amount),
      w.paymentMethod,
      w.status,
      w.notes || '—',
    ]),
    styles: { fontSize: 8 },
    headStyles: { fillColor: [139, 94, 60] },
  });

  doc.save(`lumora-owner-${finance.owner.name.toLowerCase().replace(/\s+/g, '-')}-${periodType}-${periodLabel.replace(/\s+/g, '-')}.pdf`);
};

export const generateOwnerFinancePDF = (finance) => {
  const doc = new jsPDF();
  brandHeader(doc, `Owner Financial Report — ${finance.owner.name}`);
  doc.setFontSize(10);
  doc.text(`Report Date: ${new Date().toLocaleDateString()}`, 14, 36);
  doc.text(`Total Earnings: ${formatPKR(finance.totalEarnings)}`, 14, 44);
  doc.text(`Total Withdrawn: ${formatPKR(finance.totalWithdrawn)}`, 14, 50);
  doc.text(`Available Balance: ${formatPKR(finance.availableBalance)}`, 14, 56);

  autoTable(doc, {
    startY: 64,
    head: [['Withdrawal History']],
    body: [],
    theme: 'plain',
  });

  autoTable(doc, {
    startY: 70,
    head: [['ID', 'Date', 'Amount', 'Method', 'Status', 'Notes']],
    body: finance.withdrawals.map((w) => [
      w.id,
      w.date,
      formatPKR(w.amount),
      w.paymentMethod,
      w.status,
      w.notes || '—',
    ]),
    styles: { fontSize: 8 },
    headStyles: { fillColor: [139, 94, 60] },
  });

  if (finance.monthlyEarnings?.length) {
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 10,
      head: [['Monthly Earnings Summary']],
      body: finance.monthlyEarnings.map((m) => [m.month, formatPKR(m.earnings)]),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [139, 94, 60] },
    });
  }

  doc.save(`lumora-owner-${finance.owner.name.toLowerCase()}-report.pdf`);
};

export const generateOrdersWeeklyPDF = (orders, periodLabel, category = 'Orders') => {
  const doc = new jsPDF();
  brandHeader(doc, `${category} — Weekly Report (${periodLabel})`);
  doc.setFontSize(10);
  doc.text(`Report Date: ${new Date().toLocaleDateString()}`, 14, 36);
  doc.text(`Total Orders: ${orders.length}`, 14, 42);

  autoTable(doc, {
    startY: 48,
    head: [['ID', 'Date', 'Customer', 'Products', 'Qty', 'Status', 'Total', 'Payment']],
    body: orders.map((o) => [
      `#${o.orderId}`,
      o.date,
      o.customerName,
      o.items.map((i) => i.productName).join(', '),
      o.items.reduce((s, i) => s + i.quantity, 0),
      o.status,
      formatPKR(o.orderTotal),
      o.paymentMethod,
    ]),
    styles: { fontSize: 8 },
    headStyles: { fillColor: [139, 94, 60] },
  });

  const y = doc.lastAutoTable.finalY + 16;
  doc.text('Prepared By: ___________________', 14, y);
  doc.text('Signature: ___________________', 14, y + 10);
  doc.save(`lumora-${category.toLowerCase().replace(/\s+/g, '-')}-weekly-${periodLabel}.pdf`);
};

export const generateOrdersMonthlyPDF = (orders, monthLabel, category = 'Orders', stats = {}) => {
  const doc = new jsPDF();
  brandHeader(doc, `${category} — Monthly Report (${monthLabel})`);
  doc.setFontSize(10);
  doc.text(`Report Date: ${new Date().toLocaleDateString()}`, 14, 36);
  doc.text(`Total Orders: ${orders.length}`, 14, 42);
  if (stats.revenue != null) doc.text(`Monthly Revenue: ${formatPKR(stats.revenue)}`, 14, 48);
  if (stats.productsSold != null) doc.text(`Products Sold: ${stats.productsSold}`, 14, 54);

  autoTable(doc, {
    startY: stats.revenue != null ? 60 : 48,
    head: [['ID', 'Date', 'Customer', 'Products', 'Status', 'Total', 'Payment']],
    body: orders.map((o) => [
      `#${o.orderId}`,
      o.date,
      o.customerName,
      o.items.map((i) => `${i.productName} x${i.quantity}`).join(', '),
      o.status,
      formatPKR(o.orderTotal),
      o.paymentMethod,
    ]),
    styles: { fontSize: 8 },
    headStyles: { fillColor: [139, 94, 60] },
  });

  doc.save(`lumora-${category.toLowerCase().replace(/\s+/g, '-')}-monthly-${monthLabel}.pdf`);
};

export const generateOrdersListPDF = (orders, title) => {
  const doc = new jsPDF();
  brandHeader(doc, title);
  autoTable(doc, {
    startY: 36,
    head: [['ID', 'Date', 'Customer', 'Status', 'Total']],
    body: orders.map((o) => [
      `#${o.orderId}`,
      o.date,
      o.customerName,
      o.status,
      formatPKR(o.orderTotal),
    ]),
    styles: { fontSize: 8 },
    headStyles: { fillColor: [139, 94, 60] },
  });
  doc.save(`lumora-${title.toLowerCase().replace(/\s+/g, '-')}.pdf`);
};
